import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const API_BASE = 'https://birdseye-main-326804163209.asia-southeast1.run.app';
const VIDEO_COUNT = 10;

const folders = [
  { label: 'SLER', type: 'sler' },
  { label: 'PROCESSED', type: 'processed' },
  { label: 'RAW', type: 'raw' },
];

const navIcons = {
  Dashboard: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Cameras: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  History: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="12 8 12 12 14 14"/>
      <path d="M3.05 11a9 9 0 1 0 .5-4.5"/><polyline points="3 3 3 7 7 3"/>
    </svg>
  ),
  Footage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <path d="M10 11l5 3-5 3v-6z"/>
    </svg>
  ),
  Team: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
      <path d="M17 11c1 0 2 .8 2 2v1" />
    </svg>
  ),
  Profile: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Settings: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Logout: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

const parseFilenameDate = (filename) => {
  const match = filename?.match(/^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})Z/);
  if (!match) return 'Unknown';

  const [, date, hour, minute, second] = match;
  const parsed = new Date(`${date}T${hour}:${minute}:${second}Z`);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isNewAccountEmpty = (status, body) => (
  status === 404 && JSON.stringify(body).includes('Just Started new Account?')
);

const Footage = () => {
  const navigate = useNavigate();
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) navigate('/login');
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const handleBack = () => {
    setSelectedFolder(null);
    setVideos([]);
    setError('');
    setEmpty(false);
  };

  const fetchVideos = async (folder) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    setSelectedFolder(folder);
    setLoading(true);
    setError('');
    setEmpty(false);
    setVideos([]);

    try {
      const params = new URLSearchParams({
        video_type: folder.type,
        video_count: String(VIDEO_COUNT),
      });
      const res = await fetch(`${API_BASE}/information/getlistofsavedvideos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => null);

      if (isNewAccountEmpty(res.status, body)) {
        setEmpty(true);
        return;
      }
      if (!res.ok) throw new Error(body?.detail ?? `HTTP ${res.status}`);

      const list = Array.isArray(body) ? body : [];
      setVideos(list);
      setEmpty(list.length === 0);
    } catch (err) {
      setError(err.message || 'Could not load recordings.');
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Cameras',   path: '/cameras' },
    { label: 'History',   path: '/history' },
    { label: 'Footage',   path: '/footage', active: true },
    { label: 'Team',      path: '/team' },
    { label: 'Profile',   path: '/profile' },
    { label: 'Settings',  path: '/settings' },
  ];

  return (
    <motion.div
      className="h-screen flex overflow-hidden bg-[#F5EFE7]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      <div className="w-56 flex-shrink-0 p-3">
        <div className="h-full bg-[#AC4242] rounded-3xl flex flex-col">
          <div className="p-6 flex justify-center">
            <img src="/images/logonav.png" alt="BirdsEye" className="h-32 w-auto" />
          </div>
          <nav className="flex-1 px-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-3 px-4 py-3 w-full text-left text-white rounded-xl font-poppins text-sm transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/40
                  ${item.active ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
              >
                {navIcons[item.label]}
                {item.label}
              </button>
            ))}
          </nav>
          <div className="px-4 pb-6">
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/60 hover:text-white hover:bg-white/10 rounded-xl font-poppins text-sm transition-all duration-200">
              {navIcons.Logout} Logout
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-[#F5EFE7] border-b border-black/[0.07] px-8 py-4 flex items-center justify-between flex-shrink-0">
          <img src="/images/loginlogo.png" alt="BirdsEye" className="h-12 w-auto" />
          <span className="text-xs font-poppins text-gray-400">{localStorage.getItem('userEmail') || ''}</span>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-7">
          <div className="flex items-center justify-between mb-7">
            <h1 className="text-5xl font-poppins font-bold text-[#AC4242] tracking-tight leading-none">
              Footage
            </h1>
            {selectedFolder && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2.5 bg-white border border-black/[0.08] rounded-2xl px-5 py-2.5 text-sm font-poppins font-semibold text-gray-700 shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#AC4242]/25"
              >
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            )}
          </div>

          {!selectedFolder ? (
            <div className="grid grid-cols-3 gap-5">
              {folders.map((folder, index) => (
                <motion.button
                  key={folder.type}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => fetchVideos(folder)}
                  className="bg-white rounded-3xl p-6 border border-black/[0.05] shadow-sm text-left transition-all duration-300 hover:shadow-xl hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#AC4242] focus:ring-offset-2"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#FDF0F0] flex items-center justify-center text-[#AC4242] mb-5">
                    {navIcons.Footage}
                  </div>
                  <p className="text-2xl font-poppins font-black text-gray-900 mb-1">{folder.label}</p>
                  <p className="text-xs font-poppins font-medium text-gray-400">Open saved recordings</p>
                </motion.button>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl border border-black/[0.05] overflow-hidden shadow-sm"
            >
              <div className="px-6 pt-5 pb-4 flex items-center justify-between border-b border-gray-100">
                <div>
                  <p className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400 mb-0.5">
                    {selectedFolder.label}
                  </p>
                  <p className="font-poppins font-bold text-gray-800 text-sm tracking-tight">Saved Recordings</p>
                </div>
                {loading && (
                  <div className="w-6 h-6 border-2 border-[#AC4242]/30 border-t-[#AC4242] rounded-full animate-spin" />
                )}
              </div>

              {error && (
                <div className="m-6 bg-red-50 border border-red-100 rounded-2xl px-4 py-3 text-sm font-poppins text-red-600">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="p-6 space-y-3 animate-pulse">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-gray-100 rounded-2xl" />
                  ))}
                </div>
              ) : empty ? (
                <div className="py-16 px-6 text-center">
                  <p className="text-sm font-poppins text-gray-400">No recordings found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th colSpan={2} className="p-0">
                          <div className="grid grid-cols-[minmax(0,1fr)_240px] px-6 py-4 text-left">
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400 pr-6">Video Title</span>
                            <span className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400">Date</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {videos.map((video) => (
                        <tr key={`${video.filename}-${video.url}`} className="border-b border-gray-100 last:border-0">
                          <td colSpan={2} className="p-0">
                            <button
                              onClick={() => window.open(video.url, '_blank', 'noopener,noreferrer')}
                              className="grid grid-cols-[minmax(0,1fr)_240px] w-full text-left px-6 py-4 transition-colors duration-150 hover:bg-[#FDF0F0] focus:outline-none focus:bg-[#FDF0F0]"
                            >
                              <span className="font-poppins text-sm font-semibold text-gray-800 truncate pr-6">{video.filename}</span>
                              <span className="font-poppins text-sm text-gray-500">{parseFilenameDate(video.filename)}</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Footage;
